import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import ServiceHealthBanner from "@/components/dashboard/ServiceHealthBanner";
import UKHealthWidget from "@/components/admin/UKHealthWidget";
import {
  ShieldCheck,
  Video,
  Image as ImageIcon,
  Mic,
  FolderOpen,
  LogOut,
  ExternalLink,
  Wand2,
  Key,
  Megaphone,
  Briefcase,
  PenTool,
  Coins,
  Layers,
  Film,
  BookOpen,
  Activity,
} from "lucide-react";

/**
 * AdminShell — chrome reserved for the platform owner.
 * Visually distinct from DashboardShell:
 *   - Gold-bordered sidebar, "PRIVATE ADMIN WORKSPACE" badge
 *   - No customer nav (no Pricing / no Generate-public links)
 *   - All routes scoped under /owner/...
 *   - Exit lane back to the customer app is explicit & labeled
 */
export default function AdminShell({ children, title, subtitle, breadcrumbs = [] }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const nav = [
    { to: "/owner",                  label: "Owner Console",   icon: ShieldCheck, testId: "admin-nav-console" },
    { to: "/owner/studio/video",     label: "Studio · Video",  icon: Video,       testId: "admin-nav-video" },
    { to: "/owner/studio/image",     label: "Studio · Image",  icon: ImageIcon,   testId: "admin-nav-image" },
    { to: "/owner/studio/voiceover", label: "Studio · Voice",  icon: Mic,         testId: "admin-nav-voice" },
    { to: "/owner/renders",          label: "Admin Renders",   icon: FolderOpen,  testId: "admin-nav-renders" },
    { to: "/owner/platform-keys",    label: "Platform Keys",   icon: Key,         testId: "admin-nav-platform-keys" },
    { to: "/admin/uk-health",        label: "UK Health",       icon: Activity,    testId: "admin-nav-uk-health" },
  ];

  // Customer-facing tools surfaced inside Admin so the owner can test the
  // exact Premium customer flow without leaving the workspace. The owner is
  // permanent Premium + role=owner, so every gate inside these pages is
  // already bypassed end-to-end. NEW generators added to the customer
  // dashboard MUST also be appended here to keep parity (per founder spec).
  // Customer-facing tools surfaced inside Admin. Each entry deep-links with
  // ?admin=1 — SmartShell reads that flag and renders AdminShell instead of
  // DashboardShell so the owner never loses admin chrome.
  const customerNav = [
    { to: "/generate/ad?admin=1",     label: "Ad Generator",     icon: Megaphone,  testId: "admin-nav-ad" },
    { to: "/generate/commercial?admin=1", label: "Multi-Shot Commercial", icon: Film, testId: "admin-nav-commercial" },
    { to: "/magazine?admin=1",        label: "Magazine",         icon: BookOpen,   testId: "admin-nav-magazine" },
    { to: "/wordsmith?admin=1",       label: "Wordsmith",        icon: PenTool,    testId: "admin-nav-wordsmith" },
    { to: "/generate/video?admin=1",  label: "Video Generator",  icon: Video,      testId: "admin-nav-cust-video" },
    { to: "/generate/image?admin=1",  label: "Image Generator",  icon: ImageIcon,  testId: "admin-nav-cust-image" },
    { to: "/generate/logo?admin=1",   label: "Logo Generator",   icon: Briefcase,  testId: "admin-nav-cust-logo" },
    { to: "/generate/voiceover?admin=1", label: "Voiceover",     icon: Mic,        testId: "admin-nav-cust-voice" },
    { to: "/renders?admin=1",         label: "My Renders + Smart Export", icon: Layers, testId: "admin-nav-cust-renders" },
    { to: "/settings/api-keys?admin=1", label: "BYOK · My API Keys", icon: Key,    testId: "admin-nav-cust-byok" },
    { to: "/credits?admin=1",         label: "Credits + Wallet", icon: Coins,      testId: "admin-nav-cust-credits" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen luxe-bg text-[#F4F4F0]" data-testid="admin-shell">
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden md:flex md:flex-col w-64 lg:w-72 min-h-screen border-r border-[#D4AF37]/30 bg-[#0a0a0a]/95 backdrop-blur-xl sticky top-0 sparkle-bed">
          <div className="px-6 py-7 border-b border-[#D4AF37]/30">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-[#D4AF37] gold-twinkle" strokeWidth={2} />
              <span className="block text-[10px] tracking-[0.26em] uppercase text-[#D4AF37] font-bold">
                Private Workspace
              </span>
            </div>
            <span className="block font-display text-2xl font-extrabold tracking-tight gold-shimmer">YBM Admin</span>
            <span className="block text-[10px] text-[#A0A0A0] mt-1 truncate">{user?.email}</span>
          </div>

          {/* UK health pulse — always visible to the owner */}
          <div className="px-3 py-3 border-b border-[#1F1F1F]">
            <UKHealthWidget compact />
          </div>

          <nav className="flex-1 px-3 py-6 space-y-1">
            <p className="px-4 text-[10px] uppercase tracking-[0.22em] text-[#5A5A5A] mb-3">Admin Studio</p>
            {nav.map((n) => {
              const active = location.pathname === n.to;
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testId}
                  className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                    active
                      ? "bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]"
                      : "text-[#A0A0A0] hover:text-[#F4F4F0] hover:bg-[#121212] border-l-2 border-transparent"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.7} />
                  {n.label}
                </Link>
              );
            })}

            {/* Customer-facing tools — full Premium mirror */}
            <p className="px-4 mt-6 mb-3 text-[10px] uppercase tracking-[0.22em] text-[#5A5A5A]" data-testid="admin-customer-tools-header">
              Customer Tools (Premium Mirror)
            </p>
            {customerNav.map((n) => {
              const active = location.pathname === n.to.split("?")[0];
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testId}
                  className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                    active
                      ? "bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]"
                      : "text-[#A0A0A0] hover:text-[#F4F4F0] hover:bg-[#121212] border-l-2 border-transparent"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.7} />
                  <span className="truncate">{n.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="px-3 py-5 border-t border-[#D4AF37]/30 space-y-1">
            <Link
              to="/dashboard"
              data-testid="admin-exit-to-customer"
              onClick={() => { try { window.sessionStorage.removeItem("ybm_admin_ctx"); } catch (e) {} }}
              className="flex items-center gap-3 px-4 py-2.5 text-xs text-[#A0A0A0] hover:text-[#F4F4F0] transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Exit to Customer App
            </Link>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="w-full flex items-center gap-3 px-4 py-2.5 text-xs text-[#A0A0A0] hover:text-[#D4AF37] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Log out
            </button>
          </div>
        </aside>

        {/* Mobile top bar */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#050505]/95 backdrop-blur-xl border-b border-[#D4AF37]/30 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-bold">Admin</span>
          </div>
          <button onClick={handleLogout} data-testid="admin-mobile-logout" className="text-[#A0A0A0]"><LogOut className="w-4 h-4" /></button>
        </div>

        {/* Main */}
        <main className="flex-1 min-w-0 pt-14 md:pt-0">
          {/* Mobile admin nav row — identical on iPhone and Android. Admin
              tabs first, then a vertical divider, then customer-mirror tabs.
              Horizontal scroll keeps every feature reachable in one swipe. */}
          <div className="md:hidden flex gap-1 overflow-x-auto px-4 py-3 border-b border-[#D4AF37]/30 bg-[#0a0a0a]" data-testid="admin-mobile-nav-row">
            {nav.map((n) => {
              const active = location.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`text-xs px-3 py-2 whitespace-nowrap shrink-0 ${active ? "text-[#D4AF37] border-b border-[#D4AF37]" : "text-[#A0A0A0]"}`}
                >
                  {n.label.replace("Studio · ", "")}
                </Link>
              );
            })}
            <span aria-hidden className="mx-1 self-center text-[#3A3A3A]">|</span>
            {customerNav.map((n) => {
              const active = location.pathname === n.to.split("?")[0];
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`${n.testId}-mobile`}
                  className={`text-xs px-3 py-2 whitespace-nowrap shrink-0 ${active ? "text-[#D4AF37] border-b border-[#D4AF37]" : "text-[#A0A0A0]"}`}
                >
                  {n.label.replace(" Generator", "").replace("BYOK · My API Keys", "BYOK").replace("My Renders + Smart Export", "Renders").replace("Credits + Wallet", "Credits")}
                </Link>
              );
            })}
          </div>

          <div className="max-w-6xl mx-auto px-6 md:px-10 py-8 md:py-12">
            {/* Admin top bar — always visible, never hidden */}
            <div className="mb-6 flex items-center justify-between gap-4 border-b border-[#D4AF37]/20 pb-4">
              <div className="flex items-center gap-3" data-testid="admin-top-badge">
                <span className="inline-flex items-center gap-2 px-3 py-1 border border-[#D4AF37] text-[#D4AF37] text-[10px] uppercase tracking-[0.22em] font-bold gold-glow">
                  <ShieldCheck className="w-3 h-3 gold-twinkle" /> Admin Mode · Private
                </span>
                {breadcrumbs.length > 0 && (
                  <div className="hidden sm:flex items-center gap-2 text-xs text-[#A0A0A0]" data-testid="admin-breadcrumbs">
                    {breadcrumbs.map((b, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span className="text-[#3A3A3A]">/</span>}
                        {b.to ? (
                          <Link to={b.to} className="hover:text-[#D4AF37] transition-colors">{b.label}</Link>
                        ) : (
                          <span className="text-[#F4F4F0]">{b.label}</span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
              <Wand2 className="hidden sm:block w-4 h-4 text-[#D4AF37] gold-twinkle" />
            </div>

            <ServiceHealthBanner />

            {title && (
              <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-3">{title.eyebrow || "Owner Studio"}</p>
                <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter gold-shimmer">{title.heading}</h1>
                {subtitle && <p className="text-[#A0A0A0] mt-4 max-w-2xl text-sm md:text-base">{subtitle}</p>}
              </div>
            )}

            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
